import React, { useState, useRef, useEffect, useId } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown } from 'lucide-react';

export interface NotionSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface NotionSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: NotionSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  status?: 'default' | 'success' | 'error' | 'warning';
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  searchable?: boolean;
}

const NotionSelect: React.FC<NotionSelectProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = '선택하세요',
  disabled = false,
  required = false,
  helperText,
  status = 'default',
  size = 'medium',
  fullWidth = false,
  searchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, maxHeight: 240 });
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');
  // Cleanup-A A3: 키보드 네비 — 현재 hover/focus 옵션 인덱스
  const [hoverIndex, setHoverIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ✅ 각 인스턴스마다 고유한 ID 생성 (ID 충돌 방지)
  const uniqueId = useId();

  const selectedOption = options.find(opt => opt.value === value);
  const displayText = selectedOption?.label || placeholder;

  // 검색 필터링
  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  // 크기별 스타일
  const sizeStyles = {
    xsmall: { height: '22px', fontSize: '12px', padding: '0 8px' },
    small: { height: '28px', fontSize: '14px', padding: '0 12px' },
    medium: { height: '40px', fontSize: '14px', padding: '0 14px' },
    large: { height: '48px', fontSize: '16px', padding: '0 16px' },
  };

  // 상태별 색상
  const statusColors = {
    default: { border: 'var(--ws-line)', focus: 'var(--ws-blue)' },
    success: { border: 'var(--ws-green)', focus: 'var(--ws-green)' },
    error: { border: 'var(--ws-red)', focus: 'var(--ws-red)' },
    warning: { border: 'var(--ws-orange)', focus: 'var(--ws-orange)' },
  };

  const helperTextColors = {
    default: 'var(--ws-ink-3)',
    success: 'var(--ws-green)',
    error: 'var(--ws-red)',
    warning: 'var(--ws-orange)',
  };

  // 드롭다운 위치 계산 (뷰포트 경계 고려)
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 240; // maxHeight와 동일
      const padding = 8; // 여유 공간

      // 아래쪽 가용 공간 계산
      const spaceBelow = viewportHeight - rect.bottom - padding;
      // 위쪽 가용 공간 계산
      const spaceAbove = rect.top - padding;

      // 아래 공간이 부족하고 위 공간이 더 넓으면 위로 열기
      if (spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow) {
        const actualMaxHeight = Math.min(dropdownMaxHeight, spaceAbove);
        setOpenDirection('up');
        setDropdownPosition({
          top: rect.top - actualMaxHeight - 4,
          left: rect.left,
          width: rect.width,
          maxHeight: actualMaxHeight
        });
      } else {
        const actualMaxHeight = Math.min(dropdownMaxHeight, spaceBelow);
        setOpenDirection('down');
        setDropdownPosition({
          top: rect.bottom + 4,
          left: rect.left,
          width: rect.width,
          maxHeight: actualMaxHeight
        });
      }
    }
  }, [isOpen]);

  // ✅ 외부 클릭 감지 (Portal 대응 + 이벤트 타입 통일)
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: PointerEvent) => {
      const target = event.target as HTMLElement;

      console.log('🔍 handleClickOutside fired:', {
        tagName: target.tagName,
        className: target.className,
        hasPortalAttr: target.hasAttribute('data-portal-dropdown'),
        closestPortal: target.closest('[data-portal-dropdown="notion-select"]')
      });

      // ✅ 1순위: data-portal-dropdown 속성으로 드롭다운 내부 클릭 감지
      const portalElement = target.closest('[data-portal-dropdown="notion-select"]');
      if (portalElement) {
        console.log('✅ Click inside portal dropdown - BLOCKING handleClickOutside');
        return;
      }

      // 2순위: 버튼 클릭은 무시
      if (buttonRef.current?.contains(target)) {
        console.log('✅ Click on button - ignoring');
        return;
      }

      // 3순위: Ref 체크 (추가 안전장치)
      if (dropdownRef.current?.contains(target)) {
        console.log('✅ Click on dropdownRef - ignoring');
        return;
      }

      console.log('❌ Click outside - closing dropdown');
      setIsOpen(false);
      setSearchTerm('');
    };

    // 🔴 버블링 단계에서 실행 (useCapture: false) - 버튼 핸들러가 먼저 실행되도록
    // 약간의 지연을 두어 드롭다운이 완전히 렌더링된 후 리스너 등록
    const timer = setTimeout(() => {
      document.addEventListener('pointerdown', handleClickOutside, false);
    }, 100);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('pointerdown', handleClickOutside, false);
    };
  }, [isOpen]);

  // 검색창 자동 포커스 (Portal 렌더링 후)
  useEffect(() => {
    if (isOpen && searchable) {
      // Portal이 완전히 렌더링 될 때까지 대기 후 focus
      const timer = setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select(); // 텍스트 선택으로 확실한 활성화
        }
      }, 50); // 50ms로 충분한 대기 시간 확보

      return () => clearTimeout(timer);
    }
  }, [isOpen, searchable]);

  const handleSelect = (optionValue: string) => {
    console.log('🎯 NotionSelect handleSelect called:', optionValue);
    onChange(optionValue);
    console.log('✅ NotionSelect onChange executed');

    // 🔴 드롭다운 닫기를 지연시켜 onChange가 완전히 실행되도록 보장
    setTimeout(() => {
      setIsOpen(false);
      setSearchTerm('');
      setHoverIndex(-1);
      console.log('✅ NotionSelect dropdown closed (delayed)');
    }, 0);
  };

  // Cleanup-A A3: hoverIndex 가 disabled 옵션을 건너뛰며 다음 enable 옵션으로 이동하도록 헬퍼
  const findNextEnabledIndex = (start: number, direction: 1 | -1): number => {
    if (filteredOptions.length === 0) return -1;
    let i = start;
    for (let step = 0; step < filteredOptions.length; step += 1) {
      if (i < 0) i = filteredOptions.length - 1;
      if (i >= filteredOptions.length) i = 0;
      if (!filteredOptions[i]?.disabled) return i;
      i += direction;
    }
    return -1;
  };

  // 드롭다운이 열릴 때 현재 선택된 옵션을 hoverIndex 로 동기화
  useEffect(() => {
    if (isOpen) {
      const selectedIdx = filteredOptions.findIndex((opt) => opt.value === value);
      if (selectedIdx >= 0) {
        setHoverIndex(selectedIdx);
      } else {
        setHoverIndex(findNextEnabledIndex(0, 1));
      }
    } else {
      setHoverIndex(-1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, options.length]);

  // 키보드 네비게이션 (ArrowDown/ArrowUp/Enter/Escape/Tab)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (!isOpen) {
      // 드롭다운 닫혀 있을 때 ArrowDown/Enter/Space 로 열기
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    // 드롭다운 열려 있을 때
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = findNextEnabledIndex(hoverIndex + 1, 1);
      if (next >= 0) setHoverIndex(next);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = findNextEnabledIndex(hoverIndex - 1, -1);
      if (prev >= 0) setHoverIndex(prev);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (hoverIndex >= 0 && hoverIndex < filteredOptions.length) {
        const opt = filteredOptions[hoverIndex];
        if (opt && !opt.disabled) {
          handleSelect(opt.value);
        }
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchTerm('');
      setHoverIndex(-1);
      // 트리거 버튼으로 포커스 복귀
      buttonRef.current?.focus();
    } else if (e.key === 'Tab') {
      // Tab 은 자연 포커스 흐름 유지 — 드롭다운만 닫음
      setIsOpen(false);
      setSearchTerm('');
      setHoverIndex(-1);
    }
  };

  return (
    <div
      style={{
        width: fullWidth ? '100%' : 'auto',
        opacity: disabled ? 0.6 : 1,
        marginBottom: label ? '16px' : '0'
      }}
    >
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: '6px',
            fontSize: '14px',
            fontWeight: 500,
            color: 'var(--ws-ink)'
          }}
        >
          {label}
          {required && <span style={{ color: 'var(--ws-red)' }}> *</span>}
        </label>
      )}

      <div ref={containerRef} style={{ position: 'relative' }}>
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-activedescendant={isOpen && hoverIndex >= 0 ? `${uniqueId}-opt-${hoverIndex}` : undefined}
          style={{
            width: '100%',
            ...sizeStyles[size],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: `1px solid ${statusColors[status].border}`,
            borderRadius: 'var(--ws-r-md)',
            backgroundColor: disabled ? 'var(--ws-gray-bg)' : 'var(--ws-surface)',
            boxShadow: 'var(--ws-shadow-xs)',
            color: selectedOption ? 'var(--ws-ink)' : 'var(--ws-ink-4)',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s',
            outline: 'none'
          }}
          onFocus={(e) => {
            if (!disabled) {
              e.currentTarget.style.borderColor = statusColors[status].focus;
              e.currentTarget.style.boxShadow = 'var(--ws-ring)';
            }
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = statusColors[status].border;
            e.currentTarget.style.boxShadow = 'var(--ws-shadow-xs)';
          }}
        >
          <span>{displayText}</span>
          <ChevronDown
            size={16}
            style={{
              transition: 'transform 0.2s',
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
            }}
          />
        </button>

        {/* ✅ Portal 렌더링 - DialogContent overflow 밖으로 완전히 분리 */}
        {isOpen && !disabled && ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            data-portal-dropdown="notion-select"
            role="listbox"
            id={`${uniqueId}-listbox`}
            onPointerDown={(e) => {
              console.log('📍 Dropdown onPointerDown:', e.target);
              // 🔴 stopPropagation만 실행 (preventDefault 제거!)
              e.stopPropagation();
            }}
            onClick={(e) => {
              console.log('📍 Dropdown onClick:', e.target);
              e.stopPropagation();
            }}
            onKeyDown={handleKeyDown}
            style={{
              position: 'fixed',
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
              backgroundColor: 'white',
              border: '1px solid var(--ws-line)',
              borderRadius: '8px',
              boxShadow: '0 8px 28px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.04)',
              zIndex: 9999999,
              maxHeight: `${dropdownPosition.maxHeight}px`,
              overflowY: 'auto',
              padding: '4px',
              pointerEvents: 'auto'  // 🔴 명시적으로 클릭 가능하게
            }}
          >
            {searchable && (
              <div
                style={{ padding: '4px', borderBottom: '1px solid var(--ws-line)' }}
                onPointerDown={(e) => {
                  // ✅ Dialog 포커스 트랩 우회
                  e.stopPropagation();
                }}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onPointerDown={(e) => {
                    // ✅ Dialog 포커스 트랩 우회 (preventDefault 제거로 자연스러운 포커스 허용)
                    e.stopPropagation();
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid var(--ws-line)',
                    borderRadius: '4px',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = 'var(--ws-blue)';
                    e.currentTarget.style.boxShadow = '0 0 0 2px rgba(74, 108, 250, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = 'var(--ws-line)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </div>
            )}
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, idx) => {
                const isHovered = idx === hoverIndex;
                const isSelected = option.value === value;
                const showHighlight = isHovered || isSelected;
                return (
                  <button
                    key={option.value}
                    id={`${uniqueId}-opt-${idx}`}
                    role="option"
                    aria-selected={isSelected}
                    type="button"
                    onPointerDown={(e) => {
                      console.log('🖱️ Button pointerDown:', option.value);

                      if (option.disabled) return;

                      // 🔴 즉시 선택 처리
                      handleSelect(option.value);

                      // 🔴 모든 이벤트 전파 완전 차단
                      e.preventDefault();
                      e.stopPropagation();
                      e.nativeEvent.stopImmediatePropagation();
                    }}
                    onClick={(e) => {
                      // onClick은 preventDefault로 발생하지 않지만 안전장치로 차단
                      console.log('🖱️ Button onClick (should not fire):', option.value);
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    disabled={option.disabled}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      textAlign: 'left',
                      border: 'none',
                      backgroundColor: showHighlight ? 'var(--ws-subtle)' : 'transparent',
                      color: option.disabled ? 'var(--ws-ink-4)' : 'var(--ws-ink)',
                      cursor: option.disabled ? 'not-allowed' : 'pointer',
                      borderRadius: '4px',
                      fontSize: '14px',
                      transition: 'background-color 0.15s',
                      fontWeight: isSelected ? 500 : 400,
                      pointerEvents: option.disabled ? 'none' : 'auto',  // 🔴 명시적으로 클릭 가능
                      position: 'relative',  // 🔴 z-index 적용을 위해
                      zIndex: 1  // 🔴 버튼이 위에 오도록
                    }}
                    onMouseEnter={(e) => {
                      if (!option.disabled) {
                        setHoverIndex(idx);
                        e.currentTarget.style.backgroundColor = 'var(--ws-subtle)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (option.value !== value && idx !== hoverIndex) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    {option.label}
                  </button>
                );
              })
            ) : (
              <div style={{ padding: '12px', textAlign: 'center', color: 'var(--ws-ink-4)', fontSize: '13px' }}>
                검색 결과가 없습니다
              </div>
            )}
          </div>,
          document.body
        )}
      </div>

      {helperText && (
        <div
          style={{
            marginTop: '6px',
            fontSize: '12px',
            color: helperTextColors[status]
          }}
        >
          {helperText}
        </div>
      )}
    </div>
  );
};

export default NotionSelect;
